import { useCallback, useReducer } from 'react';

import { ReportTypes } from '@showbie-socrative/socrative-utils';

import { GenerateReportEventAdapter } from '~analytics/index';
import { TeacherApplicationContext } from '~shared/context/TeacherApplicationContext';
import { ReportAnalytics } from '~teacher/components/QuizResultsHeader/ReportAnalyticsContext';
import { ReportDeliveryType } from '~teacher/enums';

import {
  downloadReports,
  emailReportsToTeacher,
  uploadToDrive,
} from '../../../../api/reports/reportsApi';
import {
  ExportReportDispatch,
  ReportOptionHookReducer,
  ReportOptionState,
  beginAsync,
  completeAsync,
  initialState,
  toDownloading,
  toEmailMe,
  toError,
  toForm,
  toPreparing,
  toSaveToDrive,
  updateOptionTo,
} from './ReportOptionHookReducer';
import { BooleanReportOption } from './ReportOptionTypes';

const useReportReducer = ({
  reportOptions,
  initialOptions,
}: {
  reportOptions: BooleanReportOption[];
  initialOptions: ReportOptionState;
}) => {
  const defaultOptions =
    initialOptions ??
    reportOptions.reduce((accumulator, { identifier }) => {
      accumulator[identifier] = initialOptions[identifier] ?? true;
      return accumulator;
    }, {});

  const startState = initialState(defaultOptions);
  const [state, dispatch] = useReducer(ReportOptionHookReducer, startState);
  return { state, dispatch };
};

type useOnPrepareDownloadCallbackParams = {
  activityInstanceId: number;
  reportAnalytics: ReportAnalytics;
  reportRequestSelectedType: ReportTypes;
  optionState: ReportOptionState;
  dispatch: ExportReportDispatch;
  appContext: TeacherApplicationContext;
  downloadReports: typeof downloadReports;
};

const useOnPrepareDownloadCallback = ({
  activityInstanceId,
  reportAnalytics,
  reportRequestSelectedType,
  optionState,
  dispatch,
  appContext,
  downloadReports,
}: useOnPrepareDownloadCallbackParams) =>
  useCallback(async () => {
    try {
      beginAsync(dispatch)();
      await downloadReports(
        {
          activityInstanceId,
          deliveryType: ReportDeliveryType.DOWNLOAD_REPORT,
          reportTypes: [reportRequestSelectedType],
        },
        appContext,
        optionState,
        (url: string) => toPreparing(dispatch)(url),
        (url: string) => toDownloading(dispatch)(url),
        (error: any) => toError(dispatch)(error)
      );
      completeAsync(dispatch)();

      new GenerateReportEventAdapter({
        deliveryMethod: 'download',
        reportType: reportRequestSelectedType,
        reportOptions: optionState,
        analytics: reportAnalytics,
      }).sendAnalytics();
    } catch (e) {
      toError(dispatch)({ payload: e });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    activityInstanceId,
    reportRequestSelectedType,
    downloadReports,
    optionState,
  ]);

type useOnUploadToDriveCallbackParams = {
  activityInstanceId: number;
  reportAnalytics: ReportAnalytics;
  reportRequestSelectedType: ReportTypes;
  optionState: ReportOptionState;
  dispatch: ExportReportDispatch;
  uploadToDrive: typeof uploadToDrive;
};

const useOnUploadToDriveCallback = ({
  activityInstanceId,
  reportAnalytics,
  reportRequestSelectedType,
  optionState,
  dispatch,
  uploadToDrive,
}: useOnUploadToDriveCallbackParams) =>
  useCallback(async () => {
    try {
      beginAsync(dispatch)();
      await uploadToDrive(
        {
          activityInstanceId,
          deliveryType: ReportDeliveryType.GOOGLE_DRIVE,
          reportTypes: [reportRequestSelectedType],
        },
        optionState
      );
      completeAsync(dispatch)();
      toSaveToDrive(dispatch)();

      new GenerateReportEventAdapter({
        deliveryMethod: 'save_to_drive',
        reportType: reportRequestSelectedType,
        reportOptions: optionState,
        analytics: reportAnalytics,
      }).sendAnalytics();
    } catch (e) {
      toError(dispatch)({ payload: e });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    activityInstanceId,
    reportRequestSelectedType,
    uploadToDrive,
    optionState,
  ]);

type useOnSendEmailToMeCallbackParams = {
  activityInstanceId: number;
  reportAnalytics: ReportAnalytics;
  reportRequestSelectedType: ReportTypes;
  optionState: ReportOptionState;
  dispatch: ExportReportDispatch;
  emailReportsToTeacher: typeof emailReportsToTeacher;
};

const useOnSendEmailToMeCallBack = ({
  activityInstanceId,
  reportAnalytics,
  reportRequestSelectedType,
  optionState,
  dispatch,
  emailReportsToTeacher,
}: useOnSendEmailToMeCallbackParams) =>
  useCallback(async () => {
    try {
      beginAsync(dispatch)();
      await emailReportsToTeacher(
        {
          activityInstanceId,
          deliveryType: ReportDeliveryType.EMAIL_REPORT,
          reportTypes: [reportRequestSelectedType],
        },
        optionState
      );
      completeAsync(dispatch)();
      toEmailMe(dispatch)();
      new GenerateReportEventAdapter({
        deliveryMethod: 'email',
        reportType: reportRequestSelectedType,
        reportOptions: optionState,
        analytics: reportAnalytics,
      }).sendAnalytics();
    } catch (e) {
      toError(dispatch)({ payload: e });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    activityInstanceId,
    reportRequestSelectedType,
    optionState,
    emailReportsToTeacher,
  ]);

export {
  toDownloading,
  toPreparing,
  updateOptionTo,
  toEmailMe,
  toSaveToDrive,
  toForm,
  toError,
  beginAsync,
  completeAsync,
  useReportReducer,
  useOnPrepareDownloadCallback,
  useOnUploadToDriveCallback,
  useOnSendEmailToMeCallBack,
};
